/**
 * Curated onboarding plan copy, keyed by billing-package code FAMILY.
 * Live backend codes carry a Shopify-tier suffix (analytics_basic,
 * replenishment_basic, replenishment_plus, ...): the `analytics*` family is
 * the Professional tier and the `replenishment*` family is Replenishment
 * Center, whatever the suffix. Mirrors assisty-website/pricing.html so
 * onboarding and marketing agree. Unknown codes -> null; callers fall back
 * to the backend description via descriptionFeatures().
 */
export interface CatalogFeature { text: string; highlight?: boolean; aiTag?: boolean; }
export interface CatalogEntry { tagline: string; introLine: string; features: CatalogFeature[]; }

export const MAX_FEATURE_LINES = 7;

export const FREE_PLAN_SUMMARY =
  '3 custom reports · 1 dashboard · basic sales analytics & inventory tracking';

const CATALOG: Record<string, CatalogEntry> = {
  analytics: {
    tagline: 'Know what\'s selling and what to reorder',
    introLine: 'Everything in Basic, plus',
    features: [
      { text: 'Demand forecasting' },
      { text: 'Reorder suggestion report' },
      { text: 'Buying calendar' },
      { text: 'Advanced inventory tracking' },
      { text: 'Advanced sales analytics' },
      { text: 'Inventory performance metrics' },
      { text: 'Hourly data importing & full export' },
    ],
  },
  replenishment: {
    tagline: 'Put your whole buying workflow on autopilot',
    introLine: 'Everything in Professional, plus',
    features: [
      { text: 'Atlas AI Assistant — ask, explain, act', highlight: true, aiTag: true },
      { text: 'Smart Buying Table — suggested quantities', highlight: true, aiTag: true },
      { text: 'Purchase orders & receiving' },
      { text: 'Advanced spreadsheet integration' },
    ],
  },
};

export function getCatalogEntry(code: string): CatalogEntry | null {
  if (!code) { return null; }
  if (code.startsWith('replenishment')) { return CATALOG.replenishment; }
  if (code.startsWith('analytics')) { return CATALOG.analytics; }
  return null;
}

export function descriptionFeatures(description: string, max: number = MAX_FEATURE_LINES): CatalogFeature[] {
  if (!description) { return []; }
  return description
    .split('\n')
    .map((text, index) => ({ text: text.trim(), index }))
    .filter((item) => item.text !== '' && item.index > 0)
    .slice(0, max)
    .map((item) => ({ text: item.text }));
}
